import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  PostTranslationsExportXmlMutationResult,
  usePostTranslationsExportXml,
} from '../../../../api/translations/translations';
import { useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import Modal from '../../../modules/Modal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  active: boolean;
  productionId: number;
  forcedExport: boolean;
  onClose: () => void;
}

/**
 * 翻訳タブのエクスポートダイアログ
 * @param param0
 * @returns
 */
function ProductionTranslationsExportModal({ active, productionId, forcedExport, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
  });

  const { handleSubmit, register, reset } = useForm({ resolver: yupResolver(schema) });

  // エクスポート処理定義
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: translationsExportXmlAsync,
    error: translationsExportXmlError,
    isLoading: translationsExportXmlIsLoading,
  } = usePostTranslationsExportXml<AxiosError<Blob>, PostTranslationsExportXmlMutationResult>(optionsForBinary);
  const [displayTranslationsExportXmlError, setDisplayTranslationsExportXmlError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setDisplayTranslationsExportXmlError(translationsExportXmlError != null);
    setIsSubmitting(false);
  }, [translationsExportXmlError]);

  // キャンセル
  const onCancelClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // エクスポート実行
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      void handleSubmit(async (data: FieldValues) => {
        const response = await translationsExportXmlAsync({
          productionId,
          data: { comment: data.comment, forced_export: forcedExport, is_trans_tab: true },
        });
        if (response.data != null) {
          executeDownload(response);
          reset();
          onClose();
        }
      })();
      setIsSubmitting(false);
    },
    [setIsSubmitting, handleSubmit, productionId, translationsExportXmlAsync, forcedExport, reset, onClose]
  );

  return (
    <Modal active={active} additionalClassName="mtsp" displayCloseButton onClose={onCancelClicked}>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="formarea1">
            <label htmlFor="">{t('コメント')}</label>
            <textarea id="" rows={10} cols={33} {...register('comment')} />
          </div>
          <div className="btnfr btnfr2 mb0">
            <div>
              <button type="button" className="text-button" onClick={() => onCancelClicked()}>
                <span className="btn2 btnbg-g mr10">
                  <span className="modal-close2">{t('キャンセル')}</span>
                </span>
              </button>
              <span>
                <input type="submit" id="" name="" value={t('エクスポート').toString()} className="btn1" />
              </span>{' '}
            </div>
          </div>
        </fieldset>
      </form>
      {translationsExportXmlIsLoading && <SpinnerModal />}
      {displayTranslationsExportXmlError && (
        <BlobErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayTranslationsExportXmlError(false);
            reset();
            onClose();
          }}
          error={translationsExportXmlError}
        />
      )}
    </Modal>
  );
}

export default ProductionTranslationsExportModal;
